import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

import type { Campaign as SegmentCampaign } from '@segment/analytics-core'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { useLocation } from 'react-router'

import { isAdminRoutePathname } from '@bettermode/domain/routes/web/admin'

import { RuntimeConfigs } from '../../../configs/runtime.js'
import { defaultLogger as logger } from '../../../logger/client/index.js'
import { extractUtmDataFromCookies } from '../utils/utm-cookies.utils.js'

const analytics = new AnalyticsBrowser()

const enabled = !!RuntimeConfigs.SEGMENT_WRITE_KEY
const ENVIRONMENT = 'Workspace'

/**
 * Add UTM tracker middleware
 *
 * analytics is a global object. Similarly, middlewares should also be
 * setup globally and outside any framework specific component or lifecycle event.
 * Or we risk registering the same middleware multiple times. Had analytics provided
 * an API to deregister middlewares, the logic could be move into the component and handled
 * as part of an effect clean up function.
 *
 * Guarded with `typeof window !== 'undefined'` because `addSourceMiddleware`
 * synchronously reads `window` at call time (via Segment's PreInitMethodCallBuffer),
 * which crashes SSR. Segment is browser-only; skipping registration on the
 * server is safe.
 */
if (typeof window !== 'undefined') {
  analytics.addSourceMiddleware(({ payload, next }) => {
    const utmData = extractUtmDataFromCookies()
    const segmentCampaign: SegmentCampaign = {
      name: utmData.utm_campaign,
      medium: utmData.utm_medium,
      source: utmData.utm_source,
      term: utmData.utm_term,
      content: utmData.utm_content,
    }
    payload.obj.context = {
      ...payload.obj.context,
      campaign: {
        ...segmentCampaign,
        ...payload.obj.context?.campaign,
      },
    }
    if (payload.obj.type === 'identify') {
      payload.obj.traits = {
        ...utmData,
        ...payload.obj.traits,
      }
    } else if (payload.obj.properties !== undefined) {
      payload.obj.properties = {
        environment: ENVIRONMENT,
        ...utmData,
        ...payload.obj.properties,
      }
    }

    next(payload)
  })
}

interface SegmentIdentifyProps {
  id: string
  email?: string
  name?: string
}

type SegmentContextType = {
  analytics: AnalyticsBrowser

  /**
   * Whether the module has been loaded.
   * `analytics.initialized` cannot be used, since that will only be set once the library finishes loading,
   * while `loaded` flag ensures the load method get called only once.
   */
  loaded: boolean

  identify?: (props: SegmentIdentifyProps) => Promise<void>
  track?: (event: string, props: unknown) => Promise<void>
  page?: (name?: string, properties?: Record<string, unknown>) => Promise<void>
  reset?: () => void
  alias?: (userId: string, previousId: string) => Promise<void>
}
const singletonContextValue: SegmentContextType = {
  analytics,
  loaded: false,
}

const loadAnalytics = () => {
  if (singletonContextValue.loaded) {
    return
  }

  singletonContextValue.loaded = true

  try {
    analytics
      .load(
        {
          writeKey: RuntimeConfigs.SEGMENT_WRITE_KEY,
        },
        {
          disable: !enabled,
          initialPageview: false,
        },
      )
      .catch(e => {
        logger.error('Segment analytics.load failed', e)
      })
  } catch (e) {
    logger.error('Segment analytics.load failed', e)
  }
}

const SegmentContext = createContext<SegmentContextType>(singletonContextValue)

interface Props {
  children: ReactNode
}

export const SegmentProvider = ({ children }: Props) => {
  const { pathname } = useLocation()
  const isAdminPage = isAdminRoutePathname(pathname)

  useEffect(() => {
    // PRIVACY / GDPR — DO NOT REMOVE THIS GUARD.
    // We must never initialize the Segment client on community (end-user)
    // pages. We are not allowed to track our customers' customers: calling
    // `analytics.load()` makes the end user's browser fetch Segment's CDN
    // settings, which discloses their IP address — personal data under GDPR —
    // to a third-party sub-processor. Only our own customers (admins/staff,
    // who live under the admin routes) may be tracked, so the client is
    // bootstrapped exclusively on admin pages. See SegmentProvider.test.tsx
    // for the regression guard.
    if (isAdminPage) {
      loadAnalytics()
    }
  }, [isAdminPage])

  const identify = useCallback(
    async ({ id, email, name }: SegmentIdentifyProps) => {
      if (!id) {
        return
      }

      try {
        await analytics.identify(id, { email, name })
      } catch (e) {
        logger.error('Segment identify failed', { id, error: e })
      }
    },
    [],
  )

  const track = useCallback(async <T = object,>(event: string, props: T) => {
    try {
      await analytics.track(event, {
        ...(props ?? {}),
      })
    } catch (e) {
      logger.error('Segment track failed', { event, error: e })
    }
  }, [])

  const page = useCallback(
    async (name?: string, properties?: Record<string, unknown>) => {
      try {
        await analytics.page(name, properties)
      } catch (e) {
        logger.error('Segment page failed', { name, error: e })
      }
    },
    [],
  )

  const reset = useCallback(() => {
    try {
      analytics.reset()
    } catch (e) {
      logger.error('Segment reset failed', e)
    }
  }, [])

  const alias = useCallback(async (userId: string, previousId: string) => {
    if (!userId || !previousId) {
      return
    }
    try {
      await analytics.alias(userId, previousId)
    } catch (e) {
      logger.error('Segment alias failed', { userId, previousId, error: e })
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      ...singletonContextValue,
      identify,
      track,
      page,
      reset,
      alias,
    }),
    [alias, identify, page, reset, track],
  )

  return (
    <SegmentContext.Provider value={contextValue}>
      {children}
    </SegmentContext.Provider>
  )
}

export const useSegment = () => {
  const context = useContext(SegmentContext)
  if (context === singletonContextValue && !RuntimeConfigs.IS_PROD) {
    logger.warn(
      'useSegment used outside SegmentProvider — telemetry calls will silently no-op',
    )
  }
  return context
}
